import React, { useEffect, useMemo, useRef, useState } from "react";
import { Snackbar, Alert, Backdrop } from "@mui/material";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Box,
  TextField,
  Stack,
  Typography,
  Avatar,
  Tooltip,
  LinearProgress,
  Divider,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import LocalMallIcon from "@mui/icons-material/LocalMall";
import TimelineIcon from "@mui/icons-material/Timeline";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BugReportIcon from "@mui/icons-material/BugReport";
import BuildIcon from "@mui/icons-material/Build";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ChecklistRtlIcon from "@mui/icons-material/ChecklistRtl";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CategoryIcon from "@mui/icons-material/Category";
import axios from "axios";
import { useProductContext } from "./ProductContextAnalysisStoc";
import WarningIcon from "@mui/icons-material/Warning";
import { FormControlLabel, Checkbox, Switch } from "@mui/material";

const normalizeApiBaseUrl = (value = "") =>
  String(value || "")
    .trim()
    .replace(/\/+$/, "");

const KINSTA_BOBO_API_BASE_URL = "https://server-9p4hx.kinsta.app";

const API_BASE_URL = normalizeApiBaseUrl(
  process.env.REACT_APP_STOC_ANALYSIS_API_BASE_URL || KINSTA_BOBO_API_BASE_URL,
);
const MASTERDATA_API_BASE = `${API_BASE_URL}/api/masterdata`;
const APP_ERRORS_API_BASE = API_BASE_URL;
const SET_COMPLET_API_BASE_URL = API_BASE_URL;
const SET_COMPLET_API_BASE = `${SET_COMPLET_API_BASE_URL}/api/masterdata/set-complet`;
const MOBILE_VISIBILITY_PROXY_URL = `${MASTERDATA_API_BASE}/luxura-products/not-visible-in-mobile`;
const MOBILE_SYNC_ONE_PROXY_URL = `${MASTERDATA_API_BASE}/luxura-products/sync-one`;
const APP_ERRORS_REQUEST_TIMEOUT_MS = 30000;
const REPAIR_REQUEST_TIMEOUT_MS = 60000;
const REPAIRABLE_APP_REASON_KEYS = new Set([
  "missing_mobile_product",
  "missing_main_category",
  "invalid_condition",
]);
const LLP_ERRORS_TAB = "llp-errors";
const LLP_BROKEN_PREFIX =
  '[vc_toggle title="Doresc produsul prin LAYAWAY" custom_font_container="tag:h5|font_size:19|text_align:left|line_height:3" custom_use_theme_fonts="yes" use_custom_heading="true" css=".vc_custom_1659790924554{margin-top: -30px !important;}"]';

const statusColors = {
  available: "green",
  out_of_stock: "red",
  discontinued: "grey",
};

const rotationFilters = [
  { label: "Mai mult de 90 zile", value: 90 },
  { label: "Mai mult de 180 zile", value: 180 },
  { label: "Mai mult de 150 zile", value: 150 },
  { label: "Mai mult de 120 zile", value: 120 },
];

const ProductTable = ({
  onProductClick,
  onCreateProductClick,
  onEditProductClick,
  onVinereaExclusivaClick,
}) => {
  const {
    products,
    setProducts,
    filteredProducts,
    setFilteredProducts,
    statuses,
    setStatuses,
    selectedStatus,
    setSelectedStatus,
    rotationFilter,
    setRotationFilter,
    skuSearch,
    setSkuSearch,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalProducts,
    setTotalProducts,
    listScrollPosition,
    setListScrollPosition,
    showStockOnly: showStockOnlyContext,
    setShowStockOnly: setShowStockOnlyContext,
    // Try to get tipVanzare from context if available
    tipVanzare,
    setTipVanzare,
  } = useProductContext() || {};

  // Dedicated state for tipAchizitie filter
  const [tipAchizitieFilter, setTipAchizitieFilter] = useState("");

  const [loading, setLoading] = useState(false);
  const [updatedToday, setUpdatedToday] = useState(false);
  const [showStockOnly, setShowStockOnly] = useState(showStockOnlyContext);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const progressIntervalRef = useRef(null);

  // Snackbar state for export success
  const [exportSuccess, setExportSuccess] = useState(false);
  const [syncSnackbar, setSyncSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [showAppErrorsPage, setShowAppErrorsPage] = useState(false);
  const [checkingAppErrors, setCheckingAppErrors] = useState(false);
  const [errorProducts, setErrorProducts] = useState([]);
  const [showSetCompletPage, setShowSetCompletPage] = useState(false);
  const [loadingSetCompletProducts, setLoadingSetCompletProducts] =
    useState(false);
  const [setCompletProducts, setSetCompletProducts] = useState([]);
  const [setCompletSearch, setSetCompletSearch] = useState("");
  const [setCompletPage, setSetCompletPage] = useState(0);
  const [setCompletRowsPerPage, setSetCompletRowsPerPage] = useState(50);
  const [setCompletTotalProducts, setSetCompletTotalProducts] = useState(0);
  const [selectedSetCompletProductIds, setSelectedSetCompletProductIds] =
    useState([]);
  const [assigningSetCompletProducts, setAssigningSetCompletProducts] =
    useState(false);
  const [setCompletProgress, setSetCompletProgress] = useState({
    total: 0,
    processed: 0,
    success: 0,
    failed: 0,
    currentSku: "",
  });
  const setCompletRequestIdRef = useRef(0);
  const [setCompletRowLoadingById, setSetCompletRowLoadingById] = useState({});
  const [hideWooUnavailableProducts, setHideWooUnavailableProducts] =
    useState(false);
  const [mobileVisibilityMeta, setMobileVisibilityMeta] = useState({
    total: 0,
    loaded: 0,
    generatedAt: "",
    failed: false,
    errorMessage: "",
  });
  const [selectedErrorProductIds, setSelectedErrorProductIds] = useState([]);
  const [repairingProducts, setRepairingProducts] = useState(false);
  const [showLlpCheckPage, setShowLlpCheckPage] = useState(false);
  const [llpTab, setLlpTab] = useState(LLP_ERRORS_TAB);
  const [checkingLlpErrors, setCheckingLlpErrors] = useState(false);
  const [llpErrorProducts, setLlpErrorProducts] = useState([]);
  const [llpScanMeta, setLlpScanMeta] = useState({
    scanned: 0,
    matched: 0,
    totalPages: 0,
  });
  const [selectedLlpProductIds, setSelectedLlpProductIds] = useState([]);
  const [llpActionLoadingById, setLlpActionLoadingById] = useState({});
  const [repairingLlpProducts, setRepairingLlpProducts] = useState(false);
  const [llpRepairProgress, setLlpRepairProgress] = useState({
    total: 0,
    processed: 0,
    success: 0,
    failed: 0,
    currentSku: "",
  });
  const [rowActionLoadingBySku, setRowActionLoadingBySku] = useState({});
  const [deleteConfirmProduct, setDeleteConfirmProduct] = useState(null);
  const [repairProgress, setRepairProgress] = useState({
    total: 0,
    processed: 0,
    success: 0,
    failed: 0,
  });

  const normalizeText = (value = "") =>
    String(value || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();

  const hasLuxuraBoutiqueCategory = (categories = []) =>
    Array.isArray(categories) &&
    categories.some((cat) =>
      normalizeText(cat?.name).includes("luxura boutique"),
    );

  const getConditionValue = (attributes = []) => {
    if (!Array.isArray(attributes)) return "";
    const conditionAttr = attributes.find((attr) => {
      const slug = normalizeText(attr?.slug);
      const name = normalizeText(attr?.name);
      return slug === "pa_conditie" || name === "conditie";
    });
    if (!conditionAttr || !Array.isArray(conditionAttr.options)) return "";
    return String(conditionAttr.options[0] || "").trim();
  };

  const hasInvalidCondition = (attributes = []) => {
    const condition = normalizeText(getConditionValue(attributes));
    return (
      condition === "foarte bun" ||
      condition === "very good" ||
      condition === "new"
    );
  };

  const getConditionRepairValue = (attributes = []) => {
    const condition = normalizeText(getConditionValue(attributes));
    if (condition === "foarte bun" || condition === "very good") {
      return "Foarte Bună";
    }
    if (condition === "new") {
      return "Nou";
    }
    return null;
  };

  const getConditionErrorLabel = (product = {}) => {
    const condition = normalizeText(product.currentCondition);
    if (condition === "new") {
      return 'Condiție Woo "New"; app cere "Nou"';
    }
    if (condition === "very good" || condition === "foarte bun") {
      return 'Condiție Woo invalidă; app cere "Foarte Bună"';
    }
    return "Condiție Woo invalidă pentru app";
  };

  const normalizeSku = (value = "") =>
    String(value || "")
      .trim()
      .toUpperCase();

  const normalizeWooProductPayload = (product = {}) => ({
    ...product,
    id: product.id,
    name: product.name || product.title || product.sku || "Produs fără nume",
    images: Array.isArray(product.images)
      ? product.images
      : product.image
        ? [{ src: product.image }]
        : [],
    regularPrice: product.regularPrice ?? product.regular_price,
    salePrice: product.salePrice ?? product.sale_price,
    stockQuantity: product.stockQuantity ?? product.stock_quantity,
    dateCreated: product.dateCreated ?? product.date_created,
    dateModified: product.dateModified ?? product.date_modified,
  });

  const mapProductToErrorRow = (product = {}) => {
    const normalizedProduct = normalizeWooProductPayload(product);
    const missingLuxuraCategory = !hasLuxuraBoutiqueCategory(
      product.categories,
    );
    const invalidCondition = hasInvalidCondition(product.attributes);
    return {
      ...normalizedProduct,
      missingLuxuraCategory,
      invalidCondition,
      currentCondition: getConditionValue(product.attributes),
      onlyInBo: false,
      sourceFlags: {
        wooValidation: true,
        mobileApp: false,
      },
      appReasons: [],
    };
  };

  const normalizeAppReasons = (reasons = [], fallbackReason = "") => {
    const rawReasons = Array.isArray(reasons) ? reasons : [];
    const normalized = rawReasons
      .map((reason) => ({
        key: String(reason?.key || reason?.label || "app_error").trim(),
        label: String(reason?.label || reason?.key || "").trim(),
      }))
      .filter((reason) => reason.label);

    if (!normalized.length && fallbackReason) {
      normalized.push({
        key: "app_error",
        label: String(fallbackReason).trim(),
      });
    }

    return normalized;
  };

  const mapMobileVisibilityDocToErrorRow = (doc = {}) => {
    const sku = String(doc.sku || "").trim();
    const normalizedSku = normalizeSku(sku);
    const appReasons = normalizeAppReasons(doc.reasons, doc.reason);
    const wooProductId = Number(doc.wooId || doc.woo_id || 0) || null;

    return {
      id: `app-${normalizedSku || wooProductId || doc.id || "unknown"}`,
      sku,
      wooProductId,
      name: doc.title || doc.name || sku || "Produs fără nume",
      title: doc.title || doc.name || "",
      images: doc.image ? [{ src: doc.image }] : [],
      status: doc.status || "",
      publishedAt: doc.publishedAt || doc.published_at || "",
      mobilePublishedAt: doc.publishedAt || doc.published_at || "",
      missingLuxuraCategory: false,
      invalidCondition: false,
      currentCondition: "",
      onlyInBo: false,
      sourceFlags: {
        wooValidation: false,
        mobileApp: true,
      },
      appReasons,
      appReason: appReasons.map((reason) => reason.label).join(", "),
    };
  };

  const getErrorProductKey = (product = {}) => {
    const sku = normalizeSku(product.sku);
    if (sku) return sku;
    const wooId = product.wooProductId || product.wooId;
    if (wooId) return `woo-${wooId}`;
    return String(product.id || "");
  };

  const mergeReasonLists = (...lists) => {
    const byKey = new Map();
    lists.flat().forEach((reason) => {
      const label = String(reason?.label || "").trim();
      if (!label) return;
      const key = String(reason?.key || label).trim();
      byKey.set(`${key}:${label}`, { key, label });
    });
    return Array.from(byKey.values());
  };

  const mergeErrorRows = (existing, incoming) => {
    if (!existing) return incoming;

    const existingSources = existing.sourceFlags || {};
    const incomingSources = incoming.sourceFlags || {};
    const sourceFlags = {
      wooValidation:
        Boolean(existingSources.wooValidation) ||
        Boolean(incomingSources.wooValidation),
      mobileApp:
        Boolean(existingSources.mobileApp) ||
        Boolean(incomingSources.mobileApp),
    };
    const appReasons = mergeReasonLists(
      existing.appReasons || [],
      incoming.appReasons || [],
    );
    const wooDetails = incomingSources.wooValidation ? incoming : existing;
    const fallbackDetails = incomingSources.wooValidation ? existing : incoming;

    return {
      ...fallbackDetails,
      ...wooDetails,
      id: wooDetails.id || fallbackDetails.id,
      sku: wooDetails.sku || fallbackDetails.sku,
      wooProductId:
        wooDetails.wooProductId ||
        fallbackDetails.wooProductId ||
        fallbackDetails.wooId ||
        null,
      name: wooDetails.name || fallbackDetails.name || fallbackDetails.title,
      images:
        Array.isArray(wooDetails.images) && wooDetails.images.length
          ? wooDetails.images
          : fallbackDetails.images || [],
      missingLuxuraCategory:
        Boolean(existing.missingLuxuraCategory) ||
        Boolean(incoming.missingLuxuraCategory),
      invalidCondition:
        Boolean(existing.invalidCondition) ||
        Boolean(incoming.invalidCondition),
      currentCondition:
        wooDetails.currentCondition || fallbackDetails.currentCondition || "",
      sourceFlags,
      appReasons,
      appReason: appReasons.map((reason) => reason.label).join(", "),
      mobilePublishedAt:
        existing.mobilePublishedAt ||
        incoming.mobilePublishedAt ||
        existing.publishedAt ||
        incoming.publishedAt ||
        "",
    };
  };

  const mergeErrorProducts = (wooRows = [], appRows = []) => {
    const byKey = new Map();

    [...wooRows, ...appRows].forEach((row) => {
      const key = getErrorProductKey(row);
      if (!key) return;
      byKey.set(key, mergeErrorRows(byKey.get(key), row));
    });

    return Array.from(byKey.values()).sort((a, b) =>
      String(a.sku || "").localeCompare(String(b.sku || "")),
    );
  };

  const isWooRepairableErrorProduct = (product = {}) =>
    Boolean(product.sourceFlags?.wooValidation) &&
    (product.missingLuxuraCategory || product.invalidCondition) &&
    !product.onlyInBo;

  const getAppReasonKeys = (product = {}) =>
    Array.isArray(product.appReasons)
      ? product.appReasons
          .map((reason) => String(reason?.key || "").trim())
          .filter(Boolean)
      : [];

  const isAppSyncRepairableErrorProduct = (product = {}) => {
    if (!product.sourceFlags?.mobileApp || product.onlyInBo) return false;
    if (!product.sku && !product.wooProductId && !product.wooId) return false;

    const reasonKeys = getAppReasonKeys(product);
    if (!reasonKeys.length) return true;
    return reasonKeys.some((key) => REPAIRABLE_APP_REASON_KEYS.has(key));
  };

  const isRepairableErrorProduct = (product = {}) =>
    isWooRepairableErrorProduct(product) ||
    isAppSyncRepairableErrorProduct(product);

  const getErrorSourceLabels = (product = {}) => {
    const labels = [];
    if (product.sourceFlags?.wooValidation) labels.push("Woo live");
    if (product.sourceFlags?.mobileApp) labels.push("App mobile");
    return labels;
  };

  const hasWooUnavailableReason = (product = {}) => {
    const reasonTexts = [
      product.reason,
      product.appReason,
      ...(Array.isArray(product.appReasons)
        ? product.appReasons.map((reason) => reason?.label)
        : []),
      ...(Array.isArray(product.reasons)
        ? product.reasons.map((reason) => reason?.label)
        : []),
    ]
      .map(normalizeText)
      .filter(Boolean);

    return reasonTexts.some(
      (reason) =>
        reason.includes("woo: nepublicat") || reason.includes("ascuns in woo"),
    );
  };

  // Export consignație products to PDF via backend endpoint
  const handleExportPDF = async () => {
    try {
      // If rotationFilter is set, append as query param
      let url = `${MASTERDATA_API_BASE}/export-consignatie-pdf`;
      if (rotationFilter) {
        url += `?rotation=${rotationFilter}`;
      }
      const response = await axios.get(url, {
        responseType: "blob",
      });
      // Create a blob and trigger download
      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", "produse-consignatie.pdf");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      setExportSuccess(true);
    } catch (error) {
      console.error("Export PDF failed:", error);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [
    page,
    rowsPerPage,
    selectedStatus,
    rotationFilter,
    skuSearch,
    showStockOnly,
    tipAchizitieFilter,
  ]);

  useEffect(() => {
    setShowStockOnly(showStockOnlyContext);
  }, [showStockOnlyContext]);

  useEffect(() => {
    const safeProducts = Array.isArray(products) ? products : [];
    const uniqueStatuses = [
      ...new Set(safeProducts.map((product) => product.status)),
    ];
    setStatuses(uniqueStatuses);
  }, [products]);

  useEffect(() => {
    checkForUpdates();
  }, []);

  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        status: selectedStatus || undefined,
        rotation: rotationFilter || undefined,
        sku: skuSearch || undefined,
      };
      if (tipAchizitieFilter && tipAchizitieFilter !== "") {
        params.tipVanzare = tipAchizitieFilter;
      }
      const response = await axios.get(
        `${MASTERDATA_API_BASE}/products-pagination`,
        {
          params,
        },
      );

      // Filtrează produsele după stockQuantity
      let filteredProducts = Array.isArray(response?.data?.products)
        ? response.data.products
        : [];
      if (showStockOnly) {
        filteredProducts = filteredProducts.filter(
          (product) => Number(product.stockQuantity) > 0,
        ); // Folosește stockQuantity > 0
      }

      setProducts(filteredProducts);
      setTotalProducts(response.data.total); // Always use response.data.total
      setFilteredProducts(filteredProducts);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkForUpdates = async () => {
    try {
      const response = await axios.get(`${MASTERDATA_API_BASE}/check-updates`); // API endpoint to check updates
      setUpdatedToday(response.data.updatedToday);
    } catch (error) {
      console.error("Error checking for updates:", error);
    }
  };

  const handleSendReport = async () => {
    try {
      await axios.post(`${MASTERDATA_API_BASE}/send-report`);
      alert("Report sent to Slack successfully!");
    } catch (error) {
      console.error("Error sending report:", error);
      alert("Failed to send report.");
    }
  };

  const handleSyncProducts = async () => {
    if (syncing) return;
    setSyncing(true);
    setSyncProgress(0);
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    progressIntervalRef.current = setInterval(() => {
      setSyncProgress((prev) => {
        if (prev >= 90) return prev;
        const increment = Math.random() * 7 + 3;
        return Math.min(prev + increment, 90);
      });
    }, 500);

    try {
      await axios.get(`${API_BASE_URL}/sync-products`);
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      setSyncProgress(100);
      setSyncSnackbar({
        open: true,
        message: "Sincronizare finalizată cu succes!",
        severity: "success",
      });
      fetchProducts();
    } catch (error) {
      console.error("Error synchronizing products:", error);
      setSyncSnackbar({
        open: true,
        message: "Eroare la sincronizarea produselor.",
        severity: "error",
      });
    } finally {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      setTimeout(() => {
        setSyncing(false);
        setSyncProgress(0);
      }, 500);
    }
  };

  const getSetCompletProductKey = (product = {}) =>
    String(product?.sku || product?.id || "");

  const updateSetCompletProductState = (updatedProduct = {}) => {
    const updatedKey = getSetCompletProductKey(updatedProduct);
    if (!updatedKey) return;

    setSetCompletProducts((prev) =>
      prev.map((product) =>
        getSetCompletProductKey(product) === updatedKey
          ? {
              ...product,
              ...updatedProduct,
              hasSetCompletCategory: true,
            }
          : product,
      ),
    );

    const patchMainProduct = (product) =>
      getSetCompletProductKey(product) === updatedKey
        ? {
            ...product,
            categories: updatedProduct.categories || product.categories,
          }
        : product;

    setProducts((prev) =>
      Array.isArray(prev) ? prev.map(patchMainProduct) : prev,
    );
    setFilteredProducts((prev) =>
      Array.isArray(prev) ? prev.map(patchMainProduct) : prev,
    );
  };

  const assignSetCompletCategory = async (product = {}) => {
    const response = await axios.post(
      `${SET_COMPLET_API_BASE}/assign`,
      {
        productId: product.id,
        sku: product.sku,
      },
      { timeout: REPAIR_REQUEST_TIMEOUT_MS },
    );
    if (response.data?.product) {
      updateSetCompletProductState(response.data.product);
    }
    return response.data;
  };

  const setSetCompletRowLoading = (productKey, isLoading) => {
    if (!productKey) return;
    setSetCompletRowLoadingById((prev) => ({
      ...prev,
      [productKey]: isLoading,
    }));
  };

  const loadSetCompletProducts = async ({ showMessage = false } = {}) => {
    const requestId = setCompletRequestIdRef.current + 1;
    setCompletRequestIdRef.current = requestId;
    setLoadingSetCompletProducts(true);
    setSelectedSetCompletProductIds([]);
    try {
      const response = await axios.get(`${SET_COMPLET_API_BASE}/products`, {
        params: {
          page: setCompletPage + 1,
          perPage: setCompletRowsPerPage,
          search: setCompletSearch.trim(),
        },
        timeout: REPAIR_REQUEST_TIMEOUT_MS,
      });
      if (requestId !== setCompletRequestIdRef.current) {
        return;
      }
      const rows = Array.isArray(response.data?.products)
        ? response.data.products
        : [];
      const meta = response.data?.meta || {};
      setSetCompletProducts(rows);
      setSetCompletTotalProducts(Number(meta.total || rows.length || 0));
      if (showMessage) {
        setSyncSnackbar({
          open: true,
          message: `Set Complet: ${rows.length} produse încărcate pe pagina curentă.`,
          severity: "success",
        });
      }
    } catch (error) {
      if (requestId !== setCompletRequestIdRef.current) {
        return;
      }
      console.error("Error loading Set Complet products:", error);
      setSyncSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Nu am putut încărca produsele pentru Set Complet.",
        severity: "error",
      });
    } finally {
      if (requestId === setCompletRequestIdRef.current) {
        setLoadingSetCompletProducts(false);
      }
    }
  };

  const handleSetCompletPageChange = (event, nextPage) => {
    if (assigningSetCompletProducts) return;
    setSetCompletPage(nextPage);
  };

  const handleSetCompletRowsPerPageChange = (event) => {
    if (assigningSetCompletProducts) return;
    setSetCompletRowsPerPage(Number(event.target.value) || 50);
    setSetCompletPage(0);
  };

  useEffect(() => {
    if (!showSetCompletPage) return undefined;

    const timeoutId = setTimeout(
      () => loadSetCompletProducts({ showMessage: false }),
      setCompletSearch.trim() ? 350 : 0,
    );

    return () => clearTimeout(timeoutId);
  }, [
    showSetCompletPage,
    setCompletPage,
    setCompletRowsPerPage,
    setCompletSearch,
  ]);

  const toggleSetCompletSelection = (productKey) => {
    setSelectedSetCompletProductIds((prev) =>
      prev.includes(productKey)
        ? prev.filter((id) => id !== productKey)
        : [...prev, productKey],
    );
  };

  const toggleSelectAllSetCompletProducts = (checked) => {
    const visibleKeys = displayedSetCompletProducts
      .map(getSetCompletProductKey)
      .filter(Boolean);
    const visibleKeySet = new Set(visibleKeys);

    setSelectedSetCompletProductIds((prev) =>
      checked
        ? Array.from(new Set([...prev, ...visibleKeys]))
        : prev.filter((id) => !visibleKeySet.has(id)),
    );
  };

  const handleSetCompletSingleProduct = async (product = {}) => {
    const productKey = getSetCompletProductKey(product);
    setSetCompletRowLoading(productKey, true);
    try {
      const result = await assignSetCompletCategory(product);
      setSyncSnackbar({
        open: true,
        message: result?.alreadySet
          ? `Produsul ${product.sku || product.id} avea deja categoria Set Complet.`
          : `Categoria Set Complet a fost atașată la ${product.sku || product.id}.`,
        severity: "success",
      });
    } catch (error) {
      console.error("Error assigning Set Complet category:", error);
      setSyncSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Nu am putut atașa categoria Set Complet.",
        severity: "error",
      });
    } finally {
      setSetCompletRowLoading(productKey, false);
    }
  };

  const handleSetCompletBulkAssign = async () => {
    if (assigningSetCompletProducts) return;

    const selectedKeys = new Set(selectedSetCompletProductIds);
    const selectedProducts = setCompletProducts.filter((product) =>
      selectedKeys.has(getSetCompletProductKey(product)),
    );

    if (!selectedProducts.length) {
      setSyncSnackbar({
        open: true,
        message: "Selectează cel puțin un produs pentru Set Complet.",
        severity: "warning",
      });
      return;
    }

    setAssigningSetCompletProducts(true);
    setSetCompletProgress({
      total: selectedProducts.length,
      processed: 0,
      success: 0,
      failed: 0,
      currentSku: "",
    });

    let success = 0;
    let failed = 0;

    for (const product of selectedProducts) {
      const productKey = getSetCompletProductKey(product);
      setSetCompletRowLoading(productKey, true);
      setSetCompletProgress((prev) => ({
        ...prev,
        currentSku: product.sku || String(product.id || ""),
      }));

      try {
        await assignSetCompletCategory(product);
        success += 1;
      } catch (error) {
        failed += 1;
        console.error(
          `Error assigning Set Complet category for ${product.sku || product.id}:`,
          error,
        );
      } finally {
        setSetCompletRowLoading(productKey, false);
        const processed = success + failed;
        setSetCompletProgress({
          total: selectedProducts.length,
          processed,
          success,
          failed,
          currentSku: product.sku || String(product.id || ""),
        });
      }
    }

    setAssigningSetCompletProducts(false);
    setSelectedSetCompletProductIds([]);
    setSyncSnackbar({
      open: true,
      message: `Set Complet finalizat: ${success} reușite, ${failed} eșuate.`,
      severity: failed ? "warning" : "success",
    });
  };

  const fetchAllWooMirrorProducts = async () => {
    const limit = 200;
    let currentPage = 1;
    let totalPages = 1;
    const allProducts = [];

    while (currentPage <= totalPages) {
      const response = await axios.get(`${APP_ERRORS_API_BASE}/products`, {
        params: { page: currentPage, limit },
        timeout: APP_ERRORS_REQUEST_TIMEOUT_MS,
      });
      const payload = response.data || {};
      const batch = Array.isArray(payload.products) ? payload.products : [];
      allProducts.push(...batch);
      totalPages = Number(payload.totalPages || totalPages || 1);
      currentPage += 1;
    }

    return allProducts;
  };

  const fetchAllWooCategories = async () => {
    const perPage = 100;
    let pageNo = 1;
    let keepLoading = true;
    const all = [];

    while (keepLoading) {
      const response = await axios.get(
        `${APP_ERRORS_API_BASE}/api/tasks/woo/categories`,
        {
          params: { per_page: perPage, page: pageNo },
          timeout: APP_ERRORS_REQUEST_TIMEOUT_MS,
        },
      );
      const rows = Array.isArray(response.data) ? response.data : [];
      all.push(...rows);
      keepLoading = rows.length === perPage;
      pageNo += 1;
    }

    return all;
  };

  const fetchWooLiveProductBySku = async (sku) => {
    if (!sku) return null;
    const response = await axios.get(
      `${APP_ERRORS_API_BASE}/api/tasks/woo/product`,
      {
        params: { sku },
        timeout: APP_ERRORS_REQUEST_TIMEOUT_MS,
      },
    );
    return response.data || null;
  };

  const fetchWooLiveProductByIdentifier = async (product = {}) => {
    const sku = String(product?.sku || "").trim();
    const wooId = Number(
      product?.wooProductId || product?.wooId || product?.id || 0,
    );
    if (!sku && !wooId) return null;
    const response = await axios.get(
      `${APP_ERRORS_API_BASE}/api/tasks/woo/product`,
      {
        params: sku ? { sku } : { id: wooId },
        timeout: APP_ERRORS_REQUEST_TIMEOUT_MS,
      },
    );
    return response.data || null;
  };

  const fetchWooLiveProductBySkuSafe = async (sku) => {
    try {
      return await fetchWooLiveProductBySku(sku);
    } catch (error) {
      if (error?.response?.status === 404) return null;
      throw error;
    }
  };

  const validateWooErrorCandidate = async (product) => {
    const sku = String(product?.sku || "").trim();
    if (!sku) return null;

    const live = await fetchWooLiveProductBySkuSafe(sku);
    if (!live?.id) return null;

    const liveRow = mapProductToErrorRow({
      ...live,
      regularPrice: live.regular_price,
      salePrice: live.sale_price,
      stockQuantity: live.stock_quantity,
      dateCreated: live.date_created,
      dateModified: live.date_modified,
    });

    if (!liveRow.missingLuxuraCategory && !liveRow.invalidCondition) {
      return null;
    }

    return {
      ...liveRow,
      onlyInBo: false,
      wooProductId: Number(live.id),
      sourceFlags: {
        wooValidation: true,
        mobileApp: false,
      },
    };
  };

  const verifyWooErrorCandidates = async (rows = []) => {
    const safeRows = Array.isArray(rows) ? rows : [];
    if (!safeRows.length) {
      return {
        rows: [],
        failed: 0,
      };
    }

    const chunkSize = 8;
    const out = [];
    let failed = 0;

    for (let i = 0; i < safeRows.length; i += chunkSize) {
      const chunk = safeRows.slice(i, i + chunkSize);
      const settled = await Promise.allSettled(
        chunk.map((product) => validateWooErrorCandidate(product)),
      );

      settled.forEach((entry) => {
        if (entry.status === "fulfilled" && entry.value) {
          out.push(entry.value);
        } else {
          failed += entry.status === "rejected" ? 1 : 0;
        }
      });
    }

    return {
      rows: out,
      failed,
    };
  };

  const fetchMobileVisibilityErrors = async (params = {}) => {
    const response = await axios.get(MOBILE_VISIBILITY_PROXY_URL, {
      params,
      timeout: APP_ERRORS_REQUEST_TIMEOUT_MS,
    });
    const payload = response.data || {};
    const docs = Array.isArray(payload.docs) ? payload.docs : [];

    return {
      generatedAt: payload.generatedAt || "",
      total: Number(payload.total || docs.length || 0),
      rows: docs
        .map(mapMobileVisibilityDocToErrorRow)
        .filter((row) => row.sku || row.wooProductId),
    };
  };

  const syncMobileProduct = async (product = {}, liveProduct = {}) => {
    const sku = String(liveProduct?.sku || product?.sku || "").trim();
    const wooId =
      Number(liveProduct?.id || product?.wooProductId || product?.wooId || 0) ||
      null;

    if (!sku && !wooId) {
      throw new Error("Produsul nu are SKU sau Woo ID pentru sync app.");
    }

    const response = await axios.post(
      MOBILE_SYNC_ONE_PROXY_URL,
      {
        ...(sku ? { sku } : {}),
        ...(wooId ? { wooId } : {}),
      },
      { timeout: REPAIR_REQUEST_TIMEOUT_MS },
    );

    return response.data || {};
  };

  const loadAppErrorProducts = async () => {
    setCheckingAppErrors(true);
    setSelectedErrorProductIds([]);

    try {
      const allProducts = await fetchAllWooMirrorProducts();
      const wooCandidates = allProducts
        .map(mapProductToErrorRow)
        .filter((p) => p.missingLuxuraCategory || p.invalidCondition);
      const wooValidation = await verifyWooErrorCandidates(wooCandidates);

      let mobileResult = {
        generatedAt: "",
        total: 0,
        rows: [],
      };
      let mobileError = null;

      try {
        mobileResult = await fetchMobileVisibilityErrors();
      } catch (error) {
        mobileError = error;
        console.error("Error loading mobile visibility errors:", error);
      }

      setMobileVisibilityMeta({
        total: mobileResult.total,
        loaded: mobileResult.rows.length,
        generatedAt: mobileResult.generatedAt,
        failed: Boolean(mobileError),
        errorMessage:
          mobileError?.response?.data?.message || mobileError?.message || "",
      });

      const combined = mergeErrorProducts(
        wooValidation.rows,
        mobileResult.rows,
      );
      setErrorProducts(combined);

      const skippedMessage = wooValidation.failed
        ? ` ${wooValidation.failed} produse Woo nu au putut fi verificate live.`
        : "";
      const appMessage = mobileError
        ? ` Endpointul app nu a răspuns: ${
            mobileError?.response?.data?.message || mobileError.message
          }`
        : ` App mobile: ${mobileResult.rows.length}.`;

      setSyncSnackbar({
        open: true,
        message: `Scan complet: ${wooValidation.rows.length} Woo live, ${appMessage} Total unic: ${combined.length}.${skippedMessage}`,
        severity: mobileError || wooValidation.failed ? "warning" : "success",
      });
    } catch (error) {
      console.error("Error loading app error products:", error);
      setSyncSnackbar({
        open: true,
        message: "Nu am putut încărca produsele pentru verificare.",
        severity: "error",
      });
    } finally {
      setCheckingAppErrors(false);
    }
  };

  const previewLlpDescription = (value = "") =>
    String(value || "")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 220);

  const setLlpRowActionLoading = (productId, isLoading) => {
    setLlpActionLoadingById((prev) => ({
      ...prev,
      [productId]: isLoading,
    }));
  };

  const loadLlpErrorProducts = async () => {
    setCheckingLlpErrors(true);
    setSelectedLlpProductIds([]);
    try {
      const response = await axios.get(
        `${APP_ERRORS_API_BASE}/api/tasks/woo/llp-errors`,
      );
      const payload = response.data || {};
      const rows = Array.isArray(payload.products) ? payload.products : [];
      setLlpErrorProducts(rows);
      setLlpScanMeta({
        scanned: Number(payload.meta?.scanned || 0),
        matched: Number(payload.meta?.matched || rows.length || 0),
        totalPages: Number(payload.meta?.totalPages || 0),
      });
      setSyncSnackbar({
        open: true,
        message: `Scan LLP finalizat: ${rows.length} produse cu prefix LLP greșit la începutul descrierii.`,
        severity: "success",
      });
    } catch (error) {
      console.error("Error loading LLP error products:", error);
      setSyncSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Nu am putut scana produsele Woo pentru erorile LLP.",
        severity: "error",
      });
    } finally {
      setCheckingLlpErrors(false);
    }
  };

  const toggleLlpSelection = (productId) => {
    setSelectedLlpProductIds((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId],
    );
  };

  const toggleSelectAllLlpProducts = (checked) => {
    setSelectedLlpProductIds(checked ? llpErrorProducts.map((p) => p.id) : []);
  };

  const fixSingleLlpProduct = async (product) => {
    const productId = Number(product?.id || 0);
    if (!productId) {
      throw new Error("Produs LLP invalid.");
    }

    const response = await axios.post(
      `${APP_ERRORS_API_BASE}/api/tasks/woo/llp-errors/${encodeURIComponent(
        productId,
      )}/fix`,
    );

    setLlpErrorProducts((prev) => prev.filter((row) => row.id !== productId));
    setSelectedLlpProductIds((prev) => prev.filter((id) => id !== productId));
    setLlpScanMeta((prev) => ({
      ...prev,
      matched: Math.max(0, Number(prev.matched || 0) - 1),
    }));

    return (
      response?.data?.message ||
      "Blocul LLP a fost repoziționat corect în descrierea Woo."
    );
  };

  const applyLlpRepair = async (scope = "selected") => {
    if (repairingLlpProducts) return;

    const targetProducts =
      scope === "all"
        ? llpErrorProducts
        : llpErrorProducts.filter((product) =>
            selectedLlpProductIds.includes(product.id),
          );

    if (!targetProducts.length) {
      setSyncSnackbar({
        open: true,
        message:
          scope === "all"
            ? "Nu există produse LLP de reparat."
            : "Nu ai selectat produse LLP pentru reparare.",
        severity: "warning",
      });
      return;
    }

    setRepairingLlpProducts(true);
    setLlpRepairProgress({
      total: targetProducts.length,
      processed: 0,
      success: 0,
      failed: 0,
      currentSku: "",
    });

    let success = 0;
    let failed = 0;
    let processed = 0;

    try {
      for (const product of targetProducts) {
        const productId = Number(product?.id || 0);
        const sku = String(product?.sku || "").trim();

        setLlpRepairProgress((prev) => ({
          ...prev,
          currentSku: sku,
        }));
        setLlpRowActionLoading(productId, true);

        try {
          await fixSingleLlpProduct(product);
          success += 1;
        } catch (error) {
          failed += 1;
          console.error(
            `Error fixing LLP prefix for product ${productId}:`,
            error,
          );
        } finally {
          setLlpRowActionLoading(productId, false);
          processed += 1;
          setLlpRepairProgress((prev) => ({
            ...prev,
            processed,
            success,
            failed,
          }));
        }
      }

      setSyncSnackbar({
        open: true,
        message: `Corecție LLP finalizată: ${success} reușite, ${failed} eșuate.`,
        severity: failed ? "warning" : "success",
      });
    } finally {
      setRepairingLlpProducts(false);
      setLlpRepairProgress((prev) => ({
        ...prev,
        currentSku: "",
      }));
    }
  };

  const handleFixLlpProduct = async (product) => {
    const productId = Number(product?.id || 0);
    if (!productId) return;

    setLlpRowActionLoading(productId, true);
    try {
      const message = await fixSingleLlpProduct(product);
      setSyncSnackbar({
        open: true,
        message,
        severity: "success",
      });
    } catch (error) {
      console.error(`Error fixing LLP prefix for product ${productId}:`, error);
      setSyncSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Nu am putut corecta descrierea produsului în Woo.",
        severity: "error",
      });
    } finally {
      setLlpRowActionLoading(productId, false);
    }
  };

  const setRowActionLoading = (sku, isLoading) => {
    setRowActionLoadingBySku((prev) => ({
      ...prev,
      [sku]: isLoading,
    }));
  };

  const handleRefreshErrorProduct = async (product) => {
    const sku = String(product?.sku || "").trim();
    if (!sku) return;

    setRowActionLoading(sku, true);
    try {
      const liveRow = await validateWooErrorCandidate(product);
      let mobileRows = [];
      let mobileError = null;

      try {
        const mobileResult = await fetchMobileVisibilityErrors({ search: sku });
        mobileRows = mobileResult.rows.filter(
          (row) => normalizeSku(row.sku) === normalizeSku(sku),
        );
      } catch (error) {
        mobileError = error;
        console.error(`Mobile refresh failed for SKU ${sku}:`, error);
      }

      const refreshedRows = mergeErrorProducts(
        liveRow ? [liveRow] : [],
        mobileRows,
      );
      const productKey = getErrorProductKey(product);
      const refreshedRow = refreshedRows[0] || null;

      if (!refreshedRow) {
        setErrorProducts((prev) =>
          prev.filter((p) => getErrorProductKey(p) !== productKey),
        );
        setSelectedErrorProductIds((prev) =>
          prev.filter((id) => id !== productKey),
        );
        setSyncSnackbar({
          open: true,
          message: mobileError
            ? `SKU ${sku} nu mai are erori Woo. Endpointul app nu a putut fi verificat.`
            : `SKU ${sku} este corect acum și a fost scos din listă.`,
          severity: mobileError ? "warning" : "success",
        });
      } else {
        setErrorProducts((prev) =>
          mergeErrorProducts(
            prev.filter((p) => getErrorProductKey(p) !== productKey),
            [refreshedRow],
          ),
        );
        setSyncSnackbar({
          open: true,
          message: mobileError
            ? `SKU ${sku} a fost revalidat în Woo. Endpointul app nu a răspuns.`
            : `SKU ${sku} a fost revalidat în Woo și app.`,
          severity: mobileError ? "warning" : "info",
        });
      }
    } catch (error) {
      console.error(`Refresh failed for SKU ${sku}:`, error);
      setSyncSnackbar({
        open: true,
        message: `Nu am putut revalida SKU ${sku}.`,
        severity: "error",
      });
    } finally {
      setRowActionLoading(sku, false);
    }
  };

  const handleDeleteErrorProduct = async (product) => {
    const sku = String(product?.sku || "").trim();
    if (!sku) return;

    setRowActionLoading(sku, true);
    try {
      await axios.delete(
        `${MASTERDATA_API_BASE}/app-errors/products/${encodeURIComponent(sku)}`,
      );
      const productKey = getErrorProductKey(product);
      setErrorProducts((prev) =>
        prev.filter((p) => getErrorProductKey(p) !== productKey),
      );
      setSelectedErrorProductIds((prev) =>
        prev.filter((id) => id !== productKey),
      );
      setSyncSnackbar({
        open: true,
        message: `SKU ${sku} a fost șters din Woo (dacă exista) și din DB.`,
        severity: "success",
      });
    } catch (error) {
      console.error(`Delete failed for SKU ${sku}:`, error);
      setSyncSnackbar({
        open: true,
        message: `Nu am putut șterge SKU ${sku}.`,
        severity: "error",
      });
    } finally {
      setRowActionLoading(sku, false);
      setDeleteConfirmProduct(null);
    }
  };

  const buildFixedProductPayload = async (product, luxuraCategoryId) => {
    const categories = Array.isArray(product.categories)
      ? product.categories
      : [];
    const attributes = Array.isArray(product.attributes)
      ? product.attributes
      : [];

    let nextCategories = categories;
    if (!hasLuxuraBoutiqueCategory(categories)) {
      const existing = categories
        .filter((cat) => cat && Number(cat.id))
        .map((cat) => ({ id: Number(cat.id) }));
      const hasIdAlready = existing.some((cat) => cat.id === luxuraCategoryId);
      nextCategories = hasIdAlready
        ? existing
        : [...existing, { id: Number(luxuraCategoryId) }];
    }

    let nextAttributes = attributes;
    const repairedCondition = getConditionRepairValue(attributes);
    if (repairedCondition) {
      nextAttributes = attributes.map((attr) => {
        const slug = normalizeText(attr?.slug);
        const name = normalizeText(attr?.name);
        const isCondition = slug === "pa_conditie" || name === "conditie";
        if (!isCondition) return attr;
        return {
          ...attr,
          options: [repairedCondition],
        };
      });
    }

    return {
      categories: nextCategories,
      attributes: nextAttributes,
    };
  };

  const revalidateRepairedProduct = async (product, liveProduct = {}) => {
    const sku = String(liveProduct?.sku || product?.sku || "").trim();
    if (!sku) return;

    const liveRow = await validateWooErrorCandidate({ sku });
    let mobileRows = [];

    try {
      const mobileResult = await fetchMobileVisibilityErrors({ search: sku });
      mobileRows = mobileResult.rows.filter(
        (row) => normalizeSku(row.sku) === normalizeSku(sku),
      );
    } catch (error) {
      console.error(`Mobile revalidation failed for SKU ${sku}:`, error);
    }

    const refreshedRows = mergeErrorProducts(
      liveRow ? [liveRow] : [],
      mobileRows,
    );
    const productKey = getErrorProductKey(product);
    const refreshedRow = refreshedRows[0] || null;

    setErrorProducts((prev) => {
      const withoutCurrent = prev.filter(
        (p) => getErrorProductKey(p) !== productKey,
      );
      return refreshedRow
        ? mergeErrorProducts(withoutCurrent, [refreshedRow])
        : withoutCurrent;
    });

    if (!refreshedRow) {
      setSelectedErrorProductIds((prev) =>
        prev.filter((id) => id !== productKey),
      );
    }
  };

  const repairSingleAppErrorProduct = async (product, getLuxuraCategoryId) => {
    const liveProduct = await fetchWooLiveProductByIdentifier(product);
    if (!liveProduct?.id) {
      throw new Error(
        `Nu am găsit produsul live în Woo pentru SKU ${product.sku}.`,
      );
    }

    const liveCategories = Array.isArray(liveProduct?.categories)
      ? liveProduct.categories
      : [];
    const liveAttributes = Array.isArray(liveProduct?.attributes)
      ? liveProduct.attributes
      : [];
    const needsCategoryRepair = !hasLuxuraBoutiqueCategory(liveCategories);
    const needsConditionRepair = hasInvalidCondition(liveAttributes);
    const resolvedWooProductId = Number(liveProduct.id);

    if (needsCategoryRepair || needsConditionRepair) {
      const luxuraCategoryId = needsCategoryRepair
        ? await getLuxuraCategoryId()
        : null;
      const payload = await buildFixedProductPayload(
        {
          ...liveProduct,
          categories: liveCategories,
          attributes: liveAttributes,
        },
        luxuraCategoryId,
      );

      await axios.put(
        `${APP_ERRORS_API_BASE}/api/tasks/woo/product/${encodeURIComponent(
          resolvedWooProductId,
        )}`,
        payload,
        { timeout: REPAIR_REQUEST_TIMEOUT_MS },
      );
    }

    let repairedLiveProduct = liveProduct;
    if (needsCategoryRepair || needsConditionRepair) {
      try {
        repairedLiveProduct = await fetchWooLiveProductByIdentifier({
          ...product,
          wooProductId: resolvedWooProductId,
        });
      } catch (error) {
        console.error(
          `Nu am putut reîncărca produsul Woo după reparație pentru SKU ${product.sku}:`,
          error,
        );
      }
    }
    const syncResult = await syncMobileProduct(product, repairedLiveProduct);

    try {
      await revalidateRepairedProduct(product, repairedLiveProduct);
    } catch (error) {
      console.error(
        `Nu am putut revalida produsul după sync pentru SKU ${product.sku}:`,
        error,
      );
    }
    return syncResult;
  };

  const applyRepair = async (scope = "selected") => {
    if (repairingProducts) return;

    const repairableProducts = displayedErrorProducts.filter(
      isRepairableErrorProduct,
    );
    const targetProducts =
      scope === "all"
        ? repairableProducts
        : repairableProducts.filter((p) =>
            selectedErrorProductIds.includes(getErrorProductKey(p)),
          );

    if (!targetProducts.length) {
      setSyncSnackbar({
        open: true,
        message:
          scope === "all"
            ? "Nu există produse Woo reparabile automat."
            : "Nu ai produse Woo reparabile selectate.",
        severity: "warning",
      });
      return;
    }

    setRepairingProducts(true);
    setRepairProgress({
      total: targetProducts.length,
      processed: 0,
      success: 0,
      failed: 0,
    });

    let success = 0;
    let failed = 0;
    let visibleAfterSync = 0;
    let notVisibleAfterSync = 0;
    let cachedLuxuraCategoryId = null;

    const getLuxuraCategoryId = async () => {
      if (cachedLuxuraCategoryId) return cachedLuxuraCategoryId;
      const categories = await fetchAllWooCategories();
      const luxuraCategory = categories.find(
        (cat) => normalizeText(cat?.name) === "luxura boutique",
      );

      if (!luxuraCategory?.id) {
        throw new Error('Categoria "Luxura Boutique" nu există în Woo.');
      }
      cachedLuxuraCategoryId = Number(luxuraCategory.id);
      return cachedLuxuraCategoryId;
    };

    try {
      for (const product of targetProducts) {
        try {
          const syncResult = await repairSingleAppErrorProduct(
            product,
            getLuxuraCategoryId,
          );
          if (syncResult?.isVisibleInMobile === false) {
            notVisibleAfterSync += 1;
          } else {
            visibleAfterSync += 1;
          }
          success += 1;
        } catch (error) {
          failed += 1;
          console.error(`Repair failed for product ${product.id}:`, {
            message: error?.message,
            status: error?.response?.status,
            data: error?.response?.data,
            sku: product?.sku,
          });
        } finally {
          setRepairProgress((prev) => ({
            ...prev,
            processed: prev.processed + 1,
            success,
            failed,
          }));
        }
      }

      setSelectedErrorProductIds([]);
      setSyncSnackbar({
        open: true,
        message: `Reparare finalizată: ${success} reușite, ${failed} eșuate. App vizibil după sync: ${visibleAfterSync}, încă blocat în app: ${notVisibleAfterSync}.`,
        severity: failed ? "warning" : "success",
      });
    } catch (error) {
      console.error("Error applying repair:", error);
      setSyncSnackbar({
        open: true,
        message: error.message || "Eroare la repararea produselor.",
        severity: "error",
      });
    } finally {
      setRepairingProducts(false);
    }
  };

  const toggleErrorSelection = (productId) => {
    setSelectedErrorProductIds((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId],
    );
  };

  const toggleSelectAllErrorProducts = (checked) => {
    setSelectedErrorProductIds(
      checked
        ? displayedErrorProducts
            .filter(isRepairableErrorProduct)
            .map((p) => getErrorProductKey(p))
        : [],
    );
  };

  const handleChangePage = (event, newPage) => {
    setListScrollPosition(0);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setListScrollPosition(0);
  };

  const getStatusColor = (status) => {
    return statusColors[status] || "blue";
  };

  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value);
    setPage(0); // Reset the page number when filter changes
    setListScrollPosition(0);
  };

  const closeSyncSnackbar = () =>
    setSyncSnackbar((prev) => ({
      ...prev,
      open: false,
    }));

  const handleRotationFilterChange = (event) => {
    setRotationFilter(event.target.value);
    setPage(0); // Reset the page number when filter changes
    setListScrollPosition(0);
  };

  const handleSkuSearchChange = (event) => {
    setSkuSearch(event.target.value);
    setPage(0); // Reset the page number when search changes
    setListScrollPosition(0);
  };

  const resetFilter = () => {
    setSelectedStatus("publish");
    setRotationFilter("");
    setSkuSearch("");
    setTipAchizitieFilter("");
    setPage(0);
    setListScrollPosition(0);
    fetchProducts();
  };

  useEffect(() => {
    if (!loading) {
      window.scrollTo({ top: listScrollPosition, behavior: "auto" });
    }
  }, [loading, listScrollPosition]);

  const handleRowClick = (product) => {
    setListScrollPosition(window.scrollY || window.pageYOffset || 0);
    onProductClick(product);
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return "—";
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return value;
    return new Intl.NumberFormat("ro-RO", {
      style: "currency",
      currency: "RON",
      maximumFractionDigits: 0,
    }).format(numeric);
  };

  const getDaysInStock = (date) => {
    if (!date) return 0;
    return Math.max(
      0,
      Math.floor(
        (Date.now() - new Date(date).getTime()) / (1000 * 60 * 60 * 24),
      ),
    );
  };

  const rotationColor = (days) => {
    if (days > 180) return "error";
    if (days > 120) return "warning";
    return "success";
  };

  const summaryStats = useMemo(() => {
    if (!filteredProducts.length) {
      return {
        totalCount: 0,
        inStockCount: 0,
        avgDays: 0,
        totalValue: 0,
      };
    }

    const totalCount = filteredProducts.length;
    const inStockCount = filteredProducts.filter(
      (product) => Number(product.stockQuantity) > 0,
    ).length;
    const totalValue = filteredProducts.reduce((acc, product) => {
      const numeric = Number(product.regularPrice || 0);
      return acc + (Number.isNaN(numeric) ? 0 : numeric);
    }, 0);
    const totalDays = filteredProducts.reduce((acc, product) => {
      return acc + getDaysInStock(product.dateCreated);
    }, 0);

    return {
      totalCount,
      inStockCount,
      totalValue,
      avgDays: Math.round(totalDays / totalCount),
    };
  }, [filteredProducts]);

  const summaryTiles = useMemo(
    () => [
      {
        label: "Produse afișate",
        value: summaryStats.totalCount,
        icon: <LocalMallIcon />,
      },
      {
        label: "Unități în stoc",
        value: summaryStats.inStockCount,
        icon: <FilterAltIcon />,
      },
      {
        label: "Medie zile în stoc",
        value: `${summaryStats.avgDays || 0} zile`,
        icon: <TimelineIcon />,
      },
      {
        label: "Valoare listată",
        value: formatCurrency(summaryStats.totalValue),
        icon: <MonetizationOnIcon />,
      },
    ],
    [summaryStats],
  );

  const llpErrorCount = llpErrorProducts.length;
  const selectedLlpCount = selectedLlpProductIds.length;
  const allLlpSelected =
    llpErrorProducts.length > 0 && selectedLlpCount === llpErrorProducts.length;
  const llpRepairProgressPercent =
    llpRepairProgress.total > 0
      ? (llpRepairProgress.processed / llpRepairProgress.total) * 100
      : 0;
  const displayedErrorProducts = hideWooUnavailableProducts
    ? errorProducts.filter((product) => !hasWooUnavailableReason(product))
    : errorProducts;
  const hiddenWooUnavailableCount =
    errorProducts.length - displayedErrorProducts.length;
  const wooValidationErrorCount = displayedErrorProducts.filter(
    (product) => product.sourceFlags?.wooValidation,
  ).length;
  const mobileAppErrorCount = displayedErrorProducts.filter(
    (product) => product.sourceFlags?.mobileApp,
  ).length;
  const repairableErrorProducts = displayedErrorProducts.filter(
    isRepairableErrorProduct,
  );
  const repairableErrorProductKeys = new Set(
    repairableErrorProducts.map((product) => getErrorProductKey(product)),
  );
  const selectedErrorCount = selectedErrorProductIds.filter((id) =>
    repairableErrorProductKeys.has(id),
  ).length;
  const allErrorsSelected =
    repairableErrorProducts.length > 0 &&
    selectedErrorCount === repairableErrorProducts.length;
  const repairProgressPercent =
    repairProgress.total > 0
      ? (repairProgress.processed / repairProgress.total) * 100
      : 0;
  const displayedSetCompletProducts = setCompletProducts;
  const displayedSetCompletKeys = new Set(
    displayedSetCompletProducts.map(getSetCompletProductKey).filter(Boolean),
  );
  const selectedSetCompletCount = selectedSetCompletProductIds.length;
  const selectedDisplayedSetCompletCount = selectedSetCompletProductIds.filter(
    (id) => displayedSetCompletKeys.has(id),
  ).length;
  const allSetCompletSelected =
    displayedSetCompletProducts.length > 0 &&
    selectedDisplayedSetCompletCount === displayedSetCompletProducts.length;
  const setCompletAlreadyAssignedCount = setCompletProducts.filter(
    (product) => product.hasSetCompletCategory,
  ).length;
  const setCompletProgressPercent =
    setCompletProgress.total > 0
      ? (setCompletProgress.processed / setCompletProgress.total) * 100
      : 0;

  if (showSetCompletPage) {
    return (
      <>
        <Stack spacing={3} sx={{ p: { xs: 2, md: 3 } }}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 4,
              boxShadow: "0 20px 60px rgba(15, 23, 42, 0.12)",
              background: "linear-gradient(125deg, #064e3b 0%, #0f766e 100%)",
              color: "white",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  Set Complet
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.86 }}>
                  Produse Woo publicate, pregătite pentru categoria Set complet.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => setShowSetCompletPage(false)}
                  disabled={assigningSetCompletProducts}
                  sx={{ borderRadius: 999, fontWeight: 700 }}
                >
                  Înapoi
                </Button>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={() => loadSetCompletProducts({ showMessage: true })}
                  disabled={
                    loadingSetCompletProducts || assigningSetCompletProducts
                  }
                  sx={{
                    borderRadius: 999,
                    fontWeight: 700,
                    bgcolor: "#ccfbf1",
                    color: "#064e3b",
                    "&:hover": { bgcolor: "#99f6e4" },
                  }}
                >
                  {loadingSetCompletProducts ? "Încarc..." : "Reîncarcă"}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<CategoryIcon />}
                  onClick={handleSetCompletBulkAssign}
                  disabled={
                    !selectedSetCompletCount || assigningSetCompletProducts
                  }
                  sx={{
                    borderRadius: 999,
                    fontWeight: 700,
                    bgcolor: "#facc15",
                    color: "#1f2937",
                    "&:hover": { bgcolor: "#fde047" },
                  }}
                >
                  Setează categoria Set Complet
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {(loadingSetCompletProducts || assigningSetCompletProducts) && (
            <Paper sx={{ p: 2.5, borderRadius: 3 }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {assigningSetCompletProducts
                    ? "Atașare categorie Set Complet"
                    : "Încărcare produse publicate"}
                </Typography>
                {assigningSetCompletProducts ? (
                  <>
                    <LinearProgress
                      variant="determinate"
                      value={setCompletProgressPercent}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Total: {setCompletProgress.total} | Procesate:{" "}
                      {setCompletProgress.processed} | Reușite:{" "}
                      {setCompletProgress.success} | Eșuate:{" "}
                      {setCompletProgress.failed}
                      {setCompletProgress.currentSku
                        ? ` | Curent: ${setCompletProgress.currentSku}`
                        : ""}
                    </Typography>
                  </>
                ) : (
                  <LinearProgress />
                )}
              </Stack>
            </Paper>
          )}

          <Paper sx={{ p: 2.5, borderRadius: 3 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Chip
                  color="primary"
                  label={`Total: ${setCompletTotalProducts}`}
                />
                <Chip
                  color="info"
                  label={`Pagina curentă: ${displayedSetCompletProducts.length}`}
                />
                <Chip
                  color="success"
                  label={`Set Complet pe pagină: ${setCompletAlreadyAssignedCount}`}
                />
                <Chip
                  color="warning"
                  label={`Selectate: ${selectedSetCompletCount}`}
                />
              </Stack>
              <TextField
                size="small"
                label="Caută produs sau SKU"
                value={setCompletSearch}
                onChange={(event) => {
                  setSetCompletSearch(event.target.value);
                  setSetCompletPage(0);
                }}
                disabled={assigningSetCompletProducts}
                sx={{
                  minWidth: { xs: "100%", md: 320 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 999,
                    backgroundColor: "white",
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Endpoint: {SET_COMPLET_API_BASE}
              </Typography>
            </Stack>
          </Paper>

          <Paper
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              boxShadow: "0 12px 32px rgba(15, 23, 42, 0.08)",
            }}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f1f5f9" }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allSetCompletSelected}
                        indeterminate={
                          selectedDisplayedSetCompletCount > 0 &&
                          selectedDisplayedSetCompletCount <
                            displayedSetCompletProducts.length
                        }
                        onChange={(event) =>
                          toggleSelectAllSetCompletProducts(
                            event.target.checked,
                          )
                        }
                        disabled={
                          !displayedSetCompletProducts.length ||
                          assigningSetCompletProducts
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Produs</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>SKU</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Categorie</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Acțiuni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedSetCompletProducts.map((product) => {
                    const productKey = getSetCompletProductKey(product);
                    const rowLoading = Boolean(
                      setCompletRowLoadingById[productKey],
                    );

                    return (
                      <TableRow key={productKey} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedSetCompletProductIds.includes(
                              productKey,
                            )}
                            onChange={() =>
                              toggleSetCompletSelection(productKey)
                            }
                            disabled={assigningSetCompletProducts}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                          >
                            <Avatar
                              variant="rounded"
                              src={product.images?.[0]?.src || ""}
                              alt={product.name}
                              sx={{ width: 56, height: 56, borderRadius: 2 }}
                            />
                            <Box>
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                dangerouslySetInnerHTML={{
                                  __html: product.name || "Produs fără nume",
                                }}
                              />
                              {product.permalink && (
                                <Typography
                                  component="a"
                                  href={product.permalink}
                                  target="_blank"
                                  rel="noreferrer"
                                  variant="caption"
                                  color="primary"
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  Vezi în Woo
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>{product.sku || "—"}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Chip
                              size="small"
                              color="success"
                              label={product.status || "publish"}
                            />
                            {product.featured && (
                              <Chip
                                size="small"
                                color="info"
                                label="featured"
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={
                              product.hasSetCompletCategory
                                ? "success"
                                : "default"
                            }
                            label={
                              product.hasSetCompletCategory
                                ? "Set Complet"
                                : "Lipsă"
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CategoryIcon />}
                            onClick={() => handleSetCompletSingleProduct(product)}
                            disabled={rowLoading || assigningSetCompletProducts}
                          >
                            {rowLoading ? "Setez..." : "Set ..."}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!displayedSetCompletProducts.length &&
                    !loadingSetCompletProducts && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Box sx={{ py: 6, textAlign: "center" }}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            {setCompletSearch.trim()
                              ? "Nu există produse pentru căutarea curentă."
                              : "Nu există produse încărcate pentru Set Complet."}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {setCompletSearch.trim()
                              ? "Schimbă termenul de căutare sau golește filtrul."
                              : "Apasă Reîncarcă pentru încărcarea produselor publicate."}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider />
            <TablePagination
              rowsPerPageOptions={[25, 50, 100, 200]}
              component="div"
              count={setCompletTotalProducts}
              rowsPerPage={setCompletRowsPerPage}
              page={setCompletPage}
              onPageChange={handleSetCompletPageChange}
              onRowsPerPageChange={handleSetCompletRowsPerPageChange}
              labelRowsPerPage="Produse pe pagină"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} din ${count}`
              }
            />
          </Paper>
        </Stack>

        <Snackbar
          open={syncSnackbar.open}
          autoHideDuration={4000}
          onClose={closeSyncSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={closeSyncSnackbar}
            severity={syncSnackbar.severity}
            sx={{ width: "100%" }}
            elevation={6}
            variant="filled"
          >
            {syncSnackbar.message}
          </Alert>
        </Snackbar>
      </>
    );
  }

  if (showLlpCheckPage) {
    return (
      <>
        <Stack spacing={3} sx={{ p: { xs: 2, md: 3 } }}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 4,
              boxShadow: "0 24px 70px rgba(88, 28, 135, 0.16)",
              background:
                "linear-gradient(135deg, #111827 0%, #3b0764 45%, #be185d 100%)",
              color: "white",
            }}
          >
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", lg: "center" }}
            >
              <Box>
                <Typography variant="h5" fontWeight={800}>
                  Check LLP
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.8, maxWidth: 760 }}
                >
                  Audităm produsele Woo care încep greșit cu blocul LLP în
                  descriere și permitem corecția directă în WooCommerce.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1.5}>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => setShowLlpCheckPage(false)}
                  sx={{ borderRadius: 999, fontWeight: 700 }}
                >
                  Înapoi
                </Button>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={loadLlpErrorProducts}
                  disabled={checkingLlpErrors}
                  sx={{
                    borderRadius: 999,
                    fontWeight: 700,
                    bgcolor: "#fce7f3",
                    color: "#4a044e",
                    "&:hover": { bgcolor: "#fbcfe8" },
                  }}
                >
                  {checkingLlpErrors ? "Scanare..." : "Re-scan LLP"}
                </Button>
              </Stack>
            </Stack>
          </Paper>

          <Paper
            sx={{
              p: 1,
              borderRadius: 4,
              boxShadow: "0 10px 28px rgba(15, 23, 42, 0.08)",
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(250,245,255,0.98) 100%)",
            }}
          >
            <Tabs
              value={llpTab}
              onChange={(_, nextValue) => setLlpTab(nextValue)}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={{
                minHeight: 56,
                "& .MuiTabs-indicator": {
                  height: 4,
                  borderRadius: 999,
                  background:
                    "linear-gradient(90deg, #7c3aed 0%, #ec4899 100%)",
                },
              }}
            >
              <Tab
                value={LLP_ERRORS_TAB}
                label="LLP errors"
                sx={{
                  minHeight: 56,
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: 16,
                }}
              />
              <Tab
                value="coming-soon"
                label="În curând"
                disabled
                sx={{ minHeight: 56, textTransform: "none", fontWeight: 700 }}
              />
            </Tabs>
          </Paper>

          {checkingLlpErrors && (
            <Paper sx={{ p: 2.5, borderRadius: 3 }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Scanare Woo live pentru prefix LLP
                </Typography>
                <LinearProgress />
              </Stack>
            </Paper>
          )}

          {llpTab === LLP_ERRORS_TAB && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Paper
                    sx={{
                      p: 2.5,
                      borderRadius: 4,
                      background:
                        "linear-gradient(135deg, rgba(244, 114, 182, 0.1), rgba(168, 85, 247, 0.12))",
                      border: "1px solid rgba(236, 72, 153, 0.12)",
                    }}
                  >
                    <Typography variant="overline" color="text.secondary">
                      Produse afectate
                    </Typography>
                    <Typography variant="h4" fontWeight={800}>
                      {llpErrorCount}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper
                    sx={{
                      p: 2.5,
                      borderRadius: 4,
                      background:
                        "linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(14, 165, 233, 0.12))",
                      border: "1px solid rgba(59, 130, 246, 0.12)",
                    }}
                  >
                    <Typography variant="overline" color="text.secondary">
                      Produse scanate live
                    </Typography>
                    <Typography variant="h4" fontWeight={800}>
                      {llpScanMeta.scanned}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper
                    sx={{
                      p: 2.5,
                      borderRadius: 4,
                      background:
                        "linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(45, 212, 191, 0.12))",
                      border: "1px solid rgba(16, 185, 129, 0.12)",
                    }}
                  >
                    <Typography variant="overline" color="text.secondary">
                      Pagini Woo parcurse
                    </Typography>
                    <Typography variant="h4" fontWeight={800}>
                      {llpScanMeta.totalPages}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Paper sx={{ p: 2.5, borderRadius: 3 }}>
                <Typography variant="subtitle1" fontWeight={800}>
                  Regula detectată
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.75, mb: 1.5 }}
                >
                  Marcăm produsele unde descrierea începe cu blocul LLP de mai
                  jos. Corecția mută blocul LLP greșit din începutul descrierii
                  și îl plasează după secțiunea{" "}
                  <strong>LIVRARE &amp; RETUR</strong>, păstrând restul
                  descrierii intact.
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 2,
                    borderRadius: 3,
                    bgcolor: "#0f172a",
                    color: "#f8fafc",
                    fontSize: 12,
                    lineHeight: 1.5,
                    overflowX: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {LLP_BROKEN_PREFIX}
                </Box>
              </Paper>

              <Paper sx={{ p: 2.5, borderRadius: 3 }}>
                <Stack
                  direction={{ xs: "column", lg: "row" }}
                  spacing={2}
                  alignItems={{ xs: "stretch", lg: "center" }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight={800}>
                      Acțiuni LLP
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Selectează produsele dorite sau rulează corecția pentru
                      toate.
                    </Typography>
                  </Box>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <Button
                      variant="outlined"
                      startIcon={<ChecklistRtlIcon />}
                      disabled={
                        repairingLlpProducts ||
                        !selectedLlpCount ||
                        !llpErrorProducts.length
                      }
                      onClick={() => applyLlpRepair("selected")}
                      sx={{ borderRadius: 999, fontWeight: 700 }}
                    >
                      Corectează selectate ({selectedLlpCount})
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<AutoFixHighIcon />}
                      disabled={
                        repairingLlpProducts || !llpErrorProducts.length
                      }
                      onClick={() => applyLlpRepair("all")}
                      sx={{
                        borderRadius: 999,
                        fontWeight: 700,
                        background:
                          "linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)",
                      }}
                    >
                      Corectează tot ({llpErrorCount})
                    </Button>
                  </Stack>
                </Stack>

                {repairingLlpProducts && (
                  <Stack spacing={1.25} sx={{ mt: 2.5 }}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      justifyContent="space-between"
                    >
                      <Typography variant="body2" fontWeight={700}>
                        Progres: {llpRepairProgress.processed} din{" "}
                        {llpRepairProgress.total}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Reușite: {llpRepairProgress.success} | Eșecuri:{" "}
                        {llpRepairProgress.failed}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={llpRepairProgressPercent}
                      sx={{ height: 10, borderRadius: 999 }}
                    />
                    {llpRepairProgress.currentSku ? (
                      <Typography variant="caption" color="text.secondary">
                        Se procesează acum: {llpRepairProgress.currentSku}
                      </Typography>
                    ) : null}
                  </Stack>
                )}
              </Paper>

              <Paper
                sx={{
                  borderRadius: 4,
                  overflow: "hidden",
                  boxShadow: "0 12px 32px rgba(15, 23, 42, 0.08)",
                }}
              >
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={allLlpSelected}
                            indeterminate={
                              selectedLlpCount > 0 &&
                              selectedLlpCount < llpErrorProducts.length
                            }
                            onChange={(event) =>
                              toggleSelectAllLlpProducts(event.target.checked)
                            }
                            disabled={
                              !llpErrorProducts.length || repairingLlpProducts
                            }
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Produs</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>SKU</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Poziție primul [vc_row]
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Preview descriere
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Acțiuni</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {llpErrorProducts.map((product) => (
                        <TableRow key={product.id} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedLlpProductIds.includes(
                                product.id,
                              )}
                              onChange={() => toggleLlpSelection(product.id)}
                              disabled={
                                repairingLlpProducts ||
                                !!llpActionLoadingById[product.id]
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Stack
                              direction="row"
                              spacing={1.5}
                              alignItems="center"
                            >
                              <Avatar
                                variant="rounded"
                                src={product.image || ""}
                                alt={product.name}
                                sx={{ width: 48, height: 48, borderRadius: 2 }}
                              />
                              <Box>
                                <Typography variant="body2" fontWeight={700}>
                                  {product.name || "Produs fără nume"}
                                </Typography>
                                {product.permalink ? (
                                  <Typography
                                    component="a"
                                    href={product.permalink}
                                    target="_blank"
                                    rel="noreferrer"
                                    variant="caption"
                                    sx={{
                                      color: "#7c3aed",
                                      textDecoration: "none",
                                      "&:hover": {
                                        textDecoration: "underline",
                                      },
                                    }}
                                  >
                                    Deschide în Woo
                                  </Typography>
                                ) : null}
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell>{product.sku || "—"}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={product.status || "—"}
                              color={
                                product.status === "publish"
                                  ? "success"
                                  : product.status === "draft"
                                    ? "warning"
                                    : "default"
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {product.firstVcRowIndex ?? "—"}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 420 }}>
                            <Typography variant="body2" color="text.secondary">
                              {previewLlpDescription(
                                product.descriptionPreview,
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="contained"
                              startIcon={<AutoFixHighIcon />}
                              onClick={() => handleFixLlpProduct(product)}
                              disabled={
                                repairingLlpProducts ||
                                !!llpActionLoadingById[product.id]
                              }
                              sx={{
                                borderRadius: 999,
                                fontWeight: 700,
                                background:
                                  "linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)",
                                boxShadow:
                                  "0 10px 24px rgba(168, 85, 247, 0.28)",
                                "&:hover": {
                                  filter: "brightness(1.03)",
                                },
                              }}
                            >
                              {llpActionLoadingById[product.id]
                                ? "Corectez..."
                                : "Corectează"}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}

                      {!llpErrorProducts.length && !checkingLlpErrors && (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <Box sx={{ py: 6, textAlign: "center" }}>
                              <Typography variant="subtitle1" fontWeight={800}>
                                Nu am găsit produse cu prefix LLP greșit la
                                începutul descrierii.
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Rulează scanarea din nou dacă vrei o verificare
                                live în WooCommerce.
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          )}
        </Stack>

        <Snackbar
          open={syncSnackbar.open}
          autoHideDuration={4000}
          onClose={closeSyncSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={closeSyncSnackbar}
            severity={syncSnackbar.severity}
            sx={{ width: "100%" }}
            elevation={6}
            variant="filled"
          >
            {syncSnackbar.message}
          </Alert>
        </Snackbar>
      </>
    );
  }

  if (showAppErrorsPage) {
    return (
      <>
        <Stack spacing={3} sx={{ p: { xs: 2, md: 3 } }}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 4,
              boxShadow: "0 20px 60px rgba(15, 23, 42, 0.12)",
              background: "linear-gradient(125deg, #0f172a 0%, #1d4ed8 100%)",
              color: "white",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  Check App Errors
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.86 }}>
                  Produse confirmate greșite în Woo live și produse raportate de
                  app ca neafișabile în mobile.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1.5}>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => setShowAppErrorsPage(false)}
                  sx={{ borderRadius: 999, fontWeight: 700 }}
                >
                  Înapoi
                </Button>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={loadAppErrorProducts}
                  disabled={checkingAppErrors || repairingProducts}
                  sx={{
                    borderRadius: 999,
                    fontWeight: 700,
                    bgcolor: "#22d3ee",
                    color: "#0f172a",
                    "&:hover": { bgcolor: "#67e8f9" },
                  }}
                >
                  {checkingAppErrors ? "Scanare..." : "Re-scan Errors"}
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {(checkingAppErrors || repairingProducts) && (
            <Paper sx={{ p: 2.5, borderRadius: 3 }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {repairingProducts
                    ? "Reparare Woo și sync app"
                    : "Scanare Woo live și app mobile"}
                </Typography>
                {repairingProducts ? (
                  <>
                    <LinearProgress
                      variant="determinate"
                      value={repairProgressPercent}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Total erori: {repairProgress.total} | Procesate:{" "}
                      {repairProgress.processed} | Reușite:{" "}
                      {repairProgress.success} | Eșuate: {repairProgress.failed}
                    </Typography>
                  </>
                ) : (
                  <LinearProgress />
                )}
              </Stack>
            </Paper>
          )}

          <Paper sx={{ p: 2.5, borderRadius: 3 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="subtitle1" fontWeight={700}>
                  Produse cu erori: {displayedErrorProducts.length}
                  {hideWooUnavailableProducts
                    ? ` / ${errorProducts.length}`
                    : ""}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Woo live: {wooValidationErrorCount} | App mobile:{" "}
                  {mobileAppErrorCount} | Reparabile:{" "}
                  {repairableErrorProducts.length} | Selectate:{" "}
                  {selectedErrorCount}
                </Typography>
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={hideWooUnavailableProducts}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setHideWooUnavailableProducts(checked);
                        if (checked) {
                          const visibleKeys = new Set(
                            errorProducts
                              .filter(
                                (product) => !hasWooUnavailableReason(product),
                              )
                              .map((product) => getErrorProductKey(product)),
                          );
                          setSelectedErrorProductIds((prev) =>
                            prev.filter((id) => visibleKeys.has(id)),
                          );
                        }
                      }}
                    />
                  }
                  label="Ascunde Woo nepublicat/ascuns"
                />
                {hideWooUnavailableProducts && (
                  <Chip
                    size="small"
                    color="default"
                    label={`Ascunse: ${hiddenWooUnavailableCount}`}
                  />
                )}
                {mobileVisibilityMeta.failed && (
                  <Chip
                    size="small"
                    color="warning"
                    label="Endpoint app indisponibil"
                  />
                )}
              </Stack>
              <Stack direction="row" spacing={1.5}>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<BuildIcon />}
                  onClick={() => applyRepair("selected")}
                  disabled={!selectedErrorCount || repairingProducts}
                  sx={{ borderRadius: 999, fontWeight: 700 }}
                >
                  Repară selectate
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<BuildIcon />}
                  onClick={() => applyRepair("all")}
                  disabled={
                    !repairableErrorProducts.length || repairingProducts
                  }
                  sx={{ borderRadius: 999, fontWeight: 700 }}
                >
                  Repară produse ({repairableErrorProducts.length})
                </Button>
              </Stack>
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1.5, display: "block" }}
            >
              Endpoint app: {MOBILE_VISIBILITY_PROXY_URL}
              {mobileVisibilityMeta.loaded
                ? ` | încărcate: ${mobileVisibilityMeta.loaded}/${mobileVisibilityMeta.total}`
                : ""}
              {mobileVisibilityMeta.failed && mobileVisibilityMeta.errorMessage
                ? ` | eroare app: ${mobileVisibilityMeta.errorMessage}`
                : ""}
            </Typography>
          </Paper>

          <Paper
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              boxShadow: "0 12px 32px rgba(15, 23, 42, 0.08)",
            }}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f1f5f9" }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allErrorsSelected}
                        indeterminate={
                          selectedErrorCount > 0 &&
                          selectedErrorCount < repairableErrorProducts.length
                        }
                        onChange={(e) =>
                          toggleSelectAllErrorProducts(e.target.checked)
                        }
                        disabled={!repairableErrorProducts.length}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Produs</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>SKU</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Sursă</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Categorie Luxura Boutique
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Condiție curentă
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Erori</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Acțiuni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedErrorProducts.map((product) => {
                    const productKey = getErrorProductKey(product);
                    const sourceLabels = getErrorSourceLabels(product);
                    const repairable = isRepairableErrorProduct(product);
                    const isAppOnly =
                      product.sourceFlags?.mobileApp &&
                      !product.sourceFlags?.wooValidation;
                    const isMixed =
                      product.sourceFlags?.mobileApp &&
                      product.sourceFlags?.wooValidation;

                    return (
                      <TableRow
                        key={productKey}
                        hover
                        sx={{
                          backgroundColor: isAppOnly
                            ? "#eff6ff"
                            : isMixed
                              ? "#fff7ed"
                              : "inherit",
                          borderLeft: isAppOnly
                            ? "4px solid #2563eb"
                            : isMixed
                              ? "4px solid #f97316"
                              : "4px solid transparent",
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedErrorProductIds.includes(
                              productKey,
                            )}
                            onChange={() => toggleErrorSelection(productKey)}
                            disabled={!repairable || repairingProducts}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                          >
                            <Avatar
                              variant="rounded"
                              src={product.images?.[0]?.src || ""}
                              alt={product.name}
                              sx={{ width: 48, height: 48, borderRadius: 2 }}
                            />
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              {product.onlyInBo && (
                                <Tooltip title="este doar in BO local, in WOO nu l am gasit.">
                                  <WarningIcon sx={{ color: "#f59e0b" }} />
                                </Tooltip>
                              )}
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                dangerouslySetInnerHTML={{
                                  __html: product.name || product.title || "—",
                                }}
                              />
                            </Stack>
                          </Stack>
                        </TableCell>
                        <TableCell>{product.sku || "—"}</TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                          >
                            {sourceLabels.map((label) => (
                              <Chip
                                key={label}
                                size="small"
                                color={
                                  label === "App mobile" ? "info" : "default"
                                }
                                label={label}
                              />
                            ))}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={
                              product.sourceFlags?.wooValidation
                                ? product.missingLuxuraCategory
                                  ? "Lipsă"
                                  : "Corect"
                                : "—"
                            }
                            color={
                              !product.sourceFlags?.wooValidation
                                ? "default"
                                : product.missingLuxuraCategory
                                  ? "error"
                                  : "success"
                            }
                          />
                        </TableCell>
                        <TableCell>{product.currentCondition || "—"}</TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                          >
                            {product.missingLuxuraCategory && (
                              <Chip
                                size="small"
                                color="error"
                                label='Fără categoria "Luxura Boutique"'
                              />
                            )}
                            {product.invalidCondition && (
                              <Chip
                                size="small"
                                color="warning"
                                label={getConditionErrorLabel(product)}
                              />
                            )}
                            {(product.appReasons || []).map((reason) => (
                              <Chip
                                key={`${reason.key}-${reason.label}`}
                                size="small"
                                color="info"
                                label={reason.label}
                              />
                            ))}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<RefreshIcon />}
                              onClick={() => handleRefreshErrorProduct(product)}
                              disabled={!!rowActionLoadingBySku[product.sku]}
                            >
                              Refresh
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteOutlineIcon />}
                              onClick={() => setDeleteConfirmProduct(product)}
                              disabled={!!rowActionLoadingBySku[product.sku]}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!displayedErrorProducts.length && !checkingAppErrors && (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Box sx={{ py: 6, textAlign: "center" }}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            {errorProducts.length
                              ? "Toate produsele găsite sunt ascunse de filtrul activ."
                              : "Nu există produse cu erori."}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {errorProducts.length
                              ? "Dezactivează filtrul dacă vrei să vezi și produsele Woo nepublicate sau ascunse."
                              : "Rulează scanarea pentru a verifica datele curente."}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>

        <Snackbar
          open={syncSnackbar.open}
          autoHideDuration={4000}
          onClose={closeSyncSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={closeSyncSnackbar}
            severity={syncSnackbar.severity}
            sx={{ width: "100%" }}
            elevation={6}
            variant="filled"
          >
            {syncSnackbar.message}
          </Alert>
        </Snackbar>
        <Dialog
          open={!!deleteConfirmProduct}
          onClose={() => {
            if (!deleteConfirmProduct?.sku)
              return setDeleteConfirmProduct(null);
            if (rowActionLoadingBySku[deleteConfirmProduct.sku]) return;
            setDeleteConfirmProduct(null);
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Confirmare ștergere</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Vrei să ștergi produsul{" "}
              <strong>{deleteConfirmProduct?.sku || "-"}</strong> din Woo (dacă
              există) și din DB `products`?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteConfirmProduct(null)}
              disabled={
                !!(
                  deleteConfirmProduct?.sku &&
                  rowActionLoadingBySku[deleteConfirmProduct.sku]
                )
              }
            >
              Anulează
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={() =>
                deleteConfirmProduct &&
                handleDeleteErrorProduct(deleteConfirmProduct)
              }
              disabled={
                !!(
                  deleteConfirmProduct?.sku &&
                  rowActionLoadingBySku[deleteConfirmProduct.sku]
                )
              }
            >
              Șterge produs
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <Stack spacing={3} sx={{ p: { xs: 2, md: 3 } }}>
        {loading && <LinearProgress color="primary" />}

        <Paper
          sx={{
            p: 3,
            borderRadius: 4,
            boxShadow: "0 20px 60px rgba(15, 23, 42, 0.12)",
            background: "linear-gradient(120deg, #0ea5e9 0%, #6366f1 100%)",
            color: "white",
          }}
        >
          <Stack spacing={3}>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Inventar Produse & Rotatie
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                Monitorizează rapid starea stocului și viteza de rotație pentru
                toate produsele listate.
              </Typography>
            </Box>
            <Grid container spacing={2}>
              {summaryTiles.map((tile) => (
                <Grid item xs={12} sm={6} md={3} key={tile.label}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      background: "rgba(255,255,255,0.15)",
                      backdropFilter: "blur(6px)",
                      color: "white",
                      minHeight: 110,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          background: "rgba(255,255,255,0.2)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {tile.icon}
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          {tile.label}
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {tile.value}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Paper>

        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 4,
            boxShadow: "0 24px 45px rgba(15, 23, 42, 0.12)",
            background: "linear-gradient(120deg, #ffffff 0%, #f3f6ff 100%)",
          }}
        >
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={3}
            alignItems={{ xs: "stretch", lg: "center" }}
            justifyContent="space-between"
          >
            <Stack
              direction={{ xs: "column", xl: "row" }}
              spacing={2}
              flexWrap="wrap"
              useFlexGap
            >
              <Paper
                elevation={0}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  borderRadius: 3,
                  border: "1px solid #e2e8f0",
                  backgroundColor: "white",
                  px: 1.5,
                  py: 1,
                }}
              >
                <FilterAltIcon sx={{ color: "#2563eb" }} />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={selectedStatus}
                    onChange={handleStatusChange}
                    label="Status"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="">
                      <em>Toate</em>
                    </MenuItem>
                    {statuses.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>

              {/* Tip vânzare filter */}
              <Paper
                elevation={0}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  borderRadius: 3,
                  border: "1px solid #e2e8f0",
                  backgroundColor: "white",
                  px: 1.5,
                  py: 1,
                }}
              >
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Tip vânzare</InputLabel>
                  <Select
                    value={tipAchizitieFilter}
                    onChange={(event) => {
                      setTipAchizitieFilter(event.target.value);
                      setPage(0);
                      setListScrollPosition(0);
                    }}
                    label="Tip vânzare"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="">
                      <em>Toate</em>
                    </MenuItem>
                    <MenuItem value="Vanzare Directa">Vânzare directă</MenuItem>
                    <MenuItem value="Consignatie">Consignație</MenuItem>
                  </Select>
                </FormControl>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  borderRadius: 3,
                  border: "1px solid #e2e8f0",
                  backgroundColor: "white",
                  px: 1.5,
                  py: 1,
                }}
              >
                <TimelineIcon sx={{ color: "#0891b2" }} />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Rotatie</InputLabel>
                  <Select
                    value={rotationFilter}
                    onChange={handleRotationFilterChange}
                    label="Rotatie"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="">
                      <em>Toate</em>
                    </MenuItem>
                    {rotationFilters.map((filter) => (
                      <MenuItem key={filter.value} value={filter.value}>
                        {filter.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  borderRadius: 3,
                  border: "1px solid #e2e8f0",
                  backgroundColor: "white",
                  px: 1.5,
                  py: 1,
                }}
              >
                <Checkbox
                  checked={showStockOnly}
                  onChange={(e) => {
                    setShowStockOnly(e.target.checked);
                    setShowStockOnlyContext(e.target.checked);
                    setPage(0);
                    setListScrollPosition(0);
                  }}
                />
                <Typography variant="body2" fontWeight={600}>
                  Doar în stoc
                </Typography>
              </Paper>

              <TextField
                variant="outlined"
                size="small"
                label="Caută SKU"
                value={skuSearch}
                onChange={handleSkuSearchChange}
                sx={{
                  minWidth: 200,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    backgroundColor: "white",
                  },
                }}
              />

              <Button
                variant="contained"
                color="inherit"
                startIcon={<RefreshIcon />}
                onClick={resetFilter}
                sx={{
                  backgroundColor: "#e2e8f0",
                  color: "#0f172a",
                  fontWeight: 600,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#cbd5f5",
                    boxShadow: "0 8px 20px rgba(148, 163, 184, 0.35)",
                  },
                }}
              >
                Resetează
              </Button>
            </Stack>

            <Stack direction="row" spacing={2} alignItems="center">
              <Tooltip title="Sincronizează produse din WooCommerce">
                <span>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleSyncProducts}
                    startIcon={<RefreshIcon />}
                    sx={{
                      borderRadius: 999,
                      px: 3,
                      fontWeight: 600,
                    }}
                    disabled={syncing}
                  >
                    Sync Produse
                  </Button>
                </span>
              </Tooltip>
              {typeof onVinereaExclusivaClick === "function" && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={onVinereaExclusivaClick}
                  startIcon={<AutoAwesomeIcon />}
                  sx={{
                    borderRadius: 999,
                    px: 3,
                    fontWeight: 600,
                  }}
                >
                  Vinerea Exclusivă
                </Button>
              )}
              <Button
                variant="contained"
                onClick={() => {
                  setShowAppErrorsPage(true);
                  if (!errorProducts.length) {
                    loadAppErrorProducts();
                  }
                }}
                startIcon={<BugReportIcon />}
                sx={{
                  borderRadius: 999,
                  px: 3,
                  fontWeight: 700,
                  background:
                    "linear-gradient(135deg, #f97316 0%, #ef4444 45%, #db2777 100%)",
                  boxShadow: "0 12px 26px rgba(239, 68, 68, 0.35)",
                  "&:hover": {
                    filter: "brightness(1.04)",
                    boxShadow: "0 14px 28px rgba(219, 39, 119, 0.4)",
                  },
                }}
              >
                Check App Errors
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setShowLlpCheckPage(true);
                  setLlpTab(LLP_ERRORS_TAB);
                  if (!llpErrorProducts.length) {
                    loadLlpErrorProducts();
                  }
                }}
                startIcon={<ChecklistRtlIcon />}
                sx={{
                  borderRadius: 999,
                  px: 3,
                  fontWeight: 700,
                  background:
                    "linear-gradient(135deg, #581c87 0%, #7c3aed 42%, #ec4899 100%)",
                  boxShadow: "0 12px 26px rgba(124, 58, 237, 0.3)",
                  "&:hover": {
                    filter: "brightness(1.04)",
                    boxShadow: "0 14px 28px rgba(124, 58, 237, 0.36)",
                  },
                }}
              >
                Check LLP
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setShowSetCompletPage(true);
                }}
                startIcon={<CategoryIcon />}
                sx={{
                  borderRadius: 999,
                  px: 3,
                  fontWeight: 700,
                  background:
                    "linear-gradient(135deg, #047857 0%, #0d9488 48%, #14b8a6 100%)",
                  boxShadow: "0 12px 26px rgba(13, 148, 136, 0.28)",
                  "&:hover": {
                    filter: "brightness(1.04)",
                    boxShadow: "0 14px 28px rgba(13, 148, 136, 0.36)",
                  },
                }}
              >
                Set Complet
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={onCreateProductClick}
                startIcon={<LocalMallIcon />}
                sx={{
                  borderRadius: 999,
                  px: 3,
                  fontWeight: 600,
                  boxShadow: "0 18px 30px rgba(37, 99, 235, 0.35)",
                }}
              >
                Produs nou
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleExportPDF}
                sx={{
                  borderRadius: 999,
                  px: 3,
                  fontWeight: 600,
                  backgroundColor: "#a21caf",
                  color: "white",
                  boxShadow: "0 8px 20px rgba(162, 28, 175, 0.18)",
                  "&:hover": {
                    backgroundColor: "#86198f",
                  },
                }}
              >
                Export Consignație PDF
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 12px 32px rgba(15, 23, 42, 0.08)",
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f1f5f9" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Produs</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Brand</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Tip achiziție</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Preț</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Rotatie</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Acțiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(Array.isArray(filteredProducts) ? filteredProducts : []).map(
                  (product) => {
                    const designer = (
                      Array.isArray(product.attributes)
                        ? product.attributes
                        : []
                    ).find((attr) => attr.name === "Designeri")?.options?.[0];
                    const days = getDaysInStock(product.dataIntrare);
                    const productKey = getSetCompletProductKey(product);
                    const setCompletRowLoading = Boolean(
                      setCompletRowLoadingById[productKey],
                    );
                    return (
                      <TableRow
                        key={product.id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleRowClick(product)}
                      >
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                          >
                            <Avatar
                              variant="rounded"
                              src={product.images[0]?.src || ""}
                              alt={product.name}
                              sx={{ width: 64, height: 64, borderRadius: 2 }}
                            />
                            <Box>
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                dangerouslySetInnerHTML={{
                                  __html: product.name,
                                }}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                SKU: {product.sku}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {designer || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            // Normalize: trim, lowercase, handle diacritics
                            let tipAchizitie = product.tip_achizitie;
                            tipAchizitie =
                              typeof tipAchizitie === "string"
                                ? tipAchizitie.trim().toLowerCase()
                                : "";
                            // Accept both "vanzare directa" and "vânzare directa"
                            if (
                              tipAchizitie === "vanzare directa" ||
                              tipAchizitie === "vânzare directa"
                            ) {
                              return (
                                <Chip
                                  label="Vânzare directă"
                                  size="small"
                                  sx={{
                                    backgroundColor: "#22c55e",
                                    color: "white",
                                    fontWeight: 600,
                                  }}
                                />
                              );
                            } else if (
                              tipAchizitie === "consignatie" ||
                              tipAchizitie === "consignație"
                            ) {
                              return (
                                <Chip
                                  label="Consignație"
                                  size="small"
                                  sx={{
                                    backgroundColor: "#a21caf",
                                    color: "white",
                                    fontWeight: 600,
                                  }}
                                />
                              );
                            } else {
                              return (
                                <Chip
                                  label="—"
                                  size="small"
                                  sx={{
                                    backgroundColor: "#e2e8f0",
                                    color: "#64748b",
                                    fontWeight: 600,
                                  }}
                                />
                              );
                            }
                          })()}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={product.status}
                            size="small"
                            sx={{
                              color: "white",
                              textTransform: "capitalize",
                              backgroundColor: getStatusColor(product.status),
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {formatCurrency(product.regularPrice)}
                          </Typography>
                          {product.salePrice && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Promo: {formatCurrency(product.salePrice)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${days} zile`}
                            size="small"
                            color={rotationColor(days)}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                setListScrollPosition(
                                  window.scrollY || window.pageYOffset || 0,
                                );
                                onEditProductClick(product);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<CategoryIcon />}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleSetCompletSingleProduct(product);
                              }}
                              disabled={setCompletRowLoading}
                            >
                              {setCompletRowLoading ? "Setez..." : "Set ..."}
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  },
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <TablePagination
            rowsPerPageOptions={[20, 40, 80, 200]}
            component="div"
            count={totalProducts}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ px: 2 }}
          />
        </Paper>
      </Stack>
      {/* Snackbar for PDF export success */}
      <Snackbar
        open={exportSuccess}
        autoHideDuration={3000}
        onClose={() => setExportSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setExportSuccess(false)}
          severity="success"
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          PDF generat cu succes!
        </Alert>
      </Snackbar>
      {/* Snackbar for sync status */}
      <Snackbar
        open={syncSnackbar.open}
        autoHideDuration={4000}
        onClose={closeSyncSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSyncSnackbar}
          severity={syncSnackbar.severity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {syncSnackbar.message}
        </Alert>
      </Snackbar>
      <Backdrop
        open={syncing}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Paper
          elevation={4}
          sx={{
            p: 4,
            borderRadius: 4,
            minWidth: 320,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Sincronizare produse
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Procesăm sincronizarea cu serverul local…
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min(syncProgress, 100)}
            sx={{ my: 2 }}
          />
          <Typography variant="subtitle1" fontWeight={600}>
            {Math.round(syncProgress)}%
          </Typography>
        </Paper>
      </Backdrop>
    </>
  );
};

export default ProductTable;
